import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Shield, Send, Lock } from 'lucide-react';

import { trackEthicsReport, sendTrackMessage } from '../../services/ethicsReportService';
import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import TextInput from '../../components/ui/TextInput';

const Container = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const Header = styled.h2`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const SecureHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding: 12px 16px;
  border-radius: 4px;
  background: rgba(16, 185, 129, 0.08);
  color: #059669;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const StatusCard = styled(Card)`
  border-left: 4px solid ${({ $color }) => $color || '#3B82F6'};
`;

const StatusLabel = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: #64748b;
`;

const StatusValue = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  font-weight: 600;
  background: ${({ $color }) => $color + '18'};
  color: ${({ $color }) => $color};
  margin-left: 8px;
`;

const MessagesContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 8px 0;
  margin-bottom: 16px;
`;

const MessageBubble = styled.div`
  max-width: 75%;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.5;
  ${({ $isOwn, theme }) =>
    $isOwn
      ? `
        margin-left: auto;
        background: ${theme.colors.primary.main};
        color: #fff;
        border-bottom-right-radius: 2px;
      `
      : `
        margin-right: auto;
        background: #F1F5F9;
        color: ${theme.colors.text.primary};
        border-bottom-left-radius: 2px;
      `
  }
`;

const MessageMeta = styled.div`
  font-size: 11px;
  color: ${({ $isOwn }) => $isOwn ? 'rgba(255,255,255,0.7)' : '#94a3b8'};
  margin-top: 4px;
`;

const MessageInputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

// #72 — yerel SendButton (kare, düz mavi) kaldırıldı; "düşük riskli" ikon
// buton + uygulamanın Tooltip'i kombinasyonu (kullanıcı talimatı) — paylaşılan
// IconButton'a bağlandı (bkz. t6-report.md).

const statusColors = {
  0: '#3B82F6', 1: '#8B5CF6', 2: '#F59E0B', 3: '#EF4444',
  4: '#10B981', 5: '#6B7280', 6: '#9CA3AF', 7: '#F97316',
};

export default function EthicsReportTrackPage() {
  const { t } = useTranslation();

  const [trackingCode, setTrackingCode] = useState('');
  const [accessPasscode, setAccessPasscode] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const statusLabels = useMemo(() => ({
    0: t('ethicsReport.statusNew'),
    1: t('ethicsReport.statusAcknowledged'),
    2: t('ethicsReport.statusUnderReview'),
    3: t('ethicsReport.statusInvestigation'),
    4: t('ethicsReport.statusActionTaken'),
    5: t('ethicsReport.statusClosed'),
    6: t('ethicsReport.statusUnfounded'),
    7: t('ethicsReport.statusAwaitingInfo'),
  }), [t]);

  useEffect(() => {
    if (reportData?.messages?.length && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [reportData?.messages?.length]);

  const handleTrack = useCallback(async () => {
    if (!trackingCode.trim() || !accessPasscode.trim()) return;
    setLoading(true);
    try {
      const res = await trackEthicsReport({ trackingCode, accessPasscode });
      const data = res.data?.data;
      if (data) setReportData(data);
    } catch {
      // error handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [trackingCode, accessPasscode]);

  const handleSendMessage = useCallback(async () => {
    if (!messageText.trim()) return;
    setSending(true);
    try {
      await sendTrackMessage({ trackingCode, accessPasscode, messageText });
      setMessageText('');
      // Refresh report data
      const res = await trackEthicsReport({ trackingCode, accessPasscode });
      const data = res.data?.data;
      if (data) setReportData(data);
    } catch {
      // error handled by interceptor
    } finally {
      setSending(false);
    }
  }, [trackingCode, accessPasscode, messageText]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  if (reportData) {
    const currentStatus = reportData.status;
    const color = statusColors[currentStatus] || '#6B7280';

    return (
      <Container>
        <SecureHeader>
          <Lock size={16} />
          {t('ethicsReport.secureConnection')}
        </SecureHeader>

        <StatusCard $color={color}>
          <StatusLabel>{t('ethicsReport.status')}</StatusLabel>
          <StatusValue $color={color}>
            {statusLabels[currentStatus] || '-'}
          </StatusValue>
        </StatusCard>

        <Card>
          <Header style={{ fontSize: 16, marginBottom: 16 }}>
            {t('ethicsReport.messages')}
          </Header>

          <MessagesContainer>
            {(reportData.messages || []).map((msg, idx) => (
              <MessageBubble key={idx} $isOwn={msg.isReporter}>
                {msg.messageText || msg.text}
                <MessageMeta $isOwn={msg.isReporter}>
                  {msg.isReporter
                    ? t('ethicsReport.you')
                    : t('ethicsReport.investigator')}
                  {msg.createDate && ` - ${new Date(msg.createDate).toLocaleString()}`}
                </MessageMeta>
              </MessageBubble>
            ))}
            <div ref={messagesEndRef} />
          </MessagesContainer>

          <MessageInputRow>
            <div style={{ flex: 1 }}>
              <TextInput
                label={t('ethicsReport.writeMessage')}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <Tooltip text={t('common.send')}>
              <IconButton
                variant="gradient"
                aria-label={t('common.send')}
                onClick={handleSendMessage}
                disabled={!messageText.trim() || sending}
              >
                <Send size={18} />
              </IconButton>
            </Tooltip>
          </MessageInputRow>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Header>{t('ethicsReport.trackReport')}</Header>

      <Card>
        <SecureHeader>
          <Shield size={16} />
          {t('ethicsReport.trackDescription')}
        </SecureHeader>

        <FieldGroup>
          <TextInput
            label={t('ethicsReport.trackingCode')}
            value={trackingCode}
            onChange={(e) => setTrackingCode(e.target.value)}
            required
          />
        </FieldGroup>

        <FieldGroup>
          <TextInput
            label={t('ethicsReport.accessPasscode')}
            value={accessPasscode}
            onChange={(e) => setAccessPasscode(e.target.value)}
            required
          />
        </FieldGroup>

        <Button onClick={handleTrack} disabled={!trackingCode.trim() || !accessPasscode.trim() || loading}>
          {loading ? '...' : t('ethicsReport.track')}
        </Button>
      </Card>
    </Container>
  );
}
